/**
 * Player-vote dispute control for auto-host voice mode (#91, D8).
 *
 * With no human to adjudicate, a wrong auto-ruling is corrected by the players: after
 * a ruling stands, any player MAY open a Dispute; that opens a vote among the *other*
 * connected players (the ruled player is excluded — they're biased) and a strict
 * majority to overturn flips the ruling (tie / no-majority leaves it standing). This
 * renders the right surface for the viewer — the Dispute call-to-action, or the open
 * vote's accept/reject controls + a live tally — all from authoritative state
 * ({@link disputeView}).
 *
 * `playerId` null (presentation / the creator's screen) → read-only: the tally and
 * status, never a vote or dispute button.
 */

import type { Command, GameState } from '@jeopardy/shared';
import { disputeView } from '../game/voice-selectors';

export interface DisputePanelProps {
  state: GameState | null;
  playerId: string | null;
  send: (command: Command) => void;
}

export function DisputePanel({
  state,
  playerId,
  send,
}: DisputePanelProps): React.JSX.Element | null {
  const view = disputeView(state, playerId);
  if (view.mode === 'none') return null;

  if (view.mode === 'canOpen') {
    return (
      <div className="dispute" data-testid="dispute-cta">
        <button
          type="button"
          className="btn"
          data-testid="dispute-open"
          onClick={() => send({ type: 'dispute', clueId: view.clueId! })}
        >
          Dispute the ruling
        </button>
      </div>
    );
  }

  // Open vote.
  const tally = view.tally ?? { accept: 0, reject: 0 };
  return (
    <div className="dispute" data-testid="dispute-vote">
      <p className="dispute__prompt">The ruling is being reviewed by the players.</p>
      <div className="dispute__tally" data-testid="dispute-tally">
        Keep: {tally.accept} · Overturn: {tally.reject}
      </div>
      {view.isRuledPlayer && (
        <p className="muted" data-testid="dispute-ruled-note">
          Your answer’s ruling is under review — the other players decide.
        </p>
      )}
      {view.canVote && (
        <div className="controls">
          <button
            type="button"
            className="btn"
            data-testid="dispute-keep"
            aria-pressed={view.myVote === true}
            onClick={() =>
              send({ type: 'vote_dispute', clueId: view.clueId!, accept: true })
            }
          >
            Keep ruling
          </button>
          <button
            type="button"
            className="btn"
            data-testid="dispute-overturn"
            aria-pressed={view.myVote === false}
            onClick={() =>
              send({ type: 'vote_dispute', clueId: view.clueId!, accept: false })
            }
          >
            Overturn
          </button>
        </div>
      )}
    </div>
  );
}
